import { Footer } from "@/components/Footer";
import { PageMeta } from "@/components/site/blocks";
import { Band, Closing, Faqs, Hero, In, List, P, Related, Src, Steps, Table, WhatIs, WorkflowCard } from "@/components/seo/kit";

// /outlook-integration: Outlook email automation with ClearMakes Inbox. Rewritten 2026-09-29
// for today's product (SEO run: gtm/products/clearmakes-inbox/seo/). Microsoft facts opened 2026-09-29.

const PATH = "/outlook-integration";

const faqs = [
  {
    q: "Does it work with Microsoft 365 work accounts?",
    a: "Yes, if your organisation allows you to connect outside apps. Some admins must approve a new app first; if Microsoft asks for admin approval when you connect, send the request to your IT admin.",
  },
  {
    q: "Is Power Automate a better choice?",
    a: "For simple Outlook-to-Teams jobs inside Microsoft 365, often yes: the basic licence is included at no cost for work or school accounts. Inbox suits people who'd rather describe the job in a sentence, or who also use Gmail, Slack or HubSpot.",
  },
  {
    q: "Can it draft replies in Outlook?",
    a: "Yes. A workflow can draft a reply for you to check or send one straight away. Start with drafts until you trust the wording.",
  },
  {
    q: "Why not just use an Outlook rule?",
    a: "Rules are free and good for sorting and forwarding. They can't tell what an email is asking, can't summarise it, and forwarding outside your organisation is often blocked in Microsoft 365.",
  },
];

const OutlookIntegration = () => (
  <>
    <PageMeta
      title="Outlook Email Automation | ClearMakes Inbox"
      description="Automate Outlook with workflows that act on new emails: draft replies, pull out details, and post to Teams, Slack or HubSpot. Set up in one sentence."
      path={PATH}
      crumbs={[
        { name: "Home", path: "/" },
        { name: "Integrations", path: "/integrations" },
        { name: "Outlook", path: PATH },
      ]}
    />
    <main>
      <Hero
        eyebrow="Integrations · Outlook"
        title="Outlook email automation, described in one sentence"
        lead="Say what should happen when a new email reaches your Outlook mailbox. Inbox drafts the reply, pulls out the details and passes the work to Teams, Slack or HubSpot."
        secondary={{ label: "See every integration", href: "/integrations" }}
      />

      <WhatIs title="What is Outlook email automation in ClearMakes Inbox?">
        Outlook email automation in ClearMakes Inbox means workflows that start when a new email arrives in an Outlook or Microsoft 365 mailbox,
        then do the routine steps: draft a reply, pull out names, amounts or dates, and post to Teams, Slack, HubSpot or Notion. You describe each
        one in a sentence and check the steps. There is a free plan.
      </WhatIs>

      <Band tone="pale" title="What an Outlook workflow can do" answer="It handles the steps that follow a new email. It reads the mailbox through a connected app, so it doesn't depend on forwarding rules.">
        <div className="grid gap-5 lg:grid-cols-2">
          <WorkflowCard
            sentence="When a customer emails about a delivery, draft a reply with the tracking link and post it to the Operations channel in Teams."
            steps={["When an email arrives in Outlook", "Check it is about a delivery", "Draft a reply", "Post to the Operations channel in Teams"]}
          />
          <WorkflowCard
            sentence="When a new enquiry reaches sales@, add them to HubSpot with their company and request."
            steps={["When an email arrives in Outlook", "Stop if it isn't a new enquiry", "Pull out the details: name, company, request", "Create the contact in HubSpot"]}
          />
        </div>
        <List
          items={[
            <>Post to Teams: <In href="/integrations/outlook-to-teams">post Outlook emails to a Teams channel</In>.</>,
            <>Post to Slack: <In href="/integrations/outlook-to-slack">Outlook to Slack automation</In>.</>,
            <>Record leads: <In href="/integrations/email-to-hubspot">add email leads to HubSpot</In>.</>,
          ]}
        />
      </Band>

      <Band tone="white" title="How to connect Outlook" answer="Sign in with Microsoft from the Apps page. A work account may ask for your admin's approval the first time.">
        <Steps
          items={[
            { title: "Create a free ClearMakes account", body: <>Start from <In href="/products/inbox">ClearMakes Inbox</In>.</> },
            { title: "Open Apps and connect Outlook", body: "Microsoft's sign-in screen shows the permissions before you agree." },
            { title: "Describe a workflow", body: "Type what should happen to new emails; answer at most one question; check the steps." },
            { title: "Switch it on", body: "Each run is listed with its steps and anything it sent." },
          ]}
        />
      </Band>

      <Band tone="pale" title="Other ways to automate Outlook" answer="Outlook rules and Power Automate are already in many Microsoft 365 subscriptions. Use them for simple sorting and copying; use Inbox when you want the job described in words or spread across other apps.">
        <Table
          caption="Ways to automate Outlook"
          head={["Way", "What it costs", "Good at", "Watch out for"]}
          rows={[
            ["Outlook rules", "Free", "Sorting, flagging, forwarding", "Can't read what an email asks; external forwarding often blocked (bounce 5.7.520)"],
            ["Power Automate", "Free licence for work or school accounts with standard connectors; Premium $15 a user a month, paid yearly", "Outlook and Teams flows inside Microsoft 365; trigger “When a new email arrives (V3)”", "You build each flow from triggers and actions; premium connectors cost extra"],
            ["IFTTT", "Pro $2.99 a month", "One simple rule", "Outlook trigger polled hourly on Free, every 5 minutes on Pro"],
            ["ClearMakes Inbox", "Free: 3 apps, 100 runs a month. Starter: $29 a month for 5,000 runs", "One sentence; replies, details and other apps in one run", "Only starts from messages; newer company"],
          ]}
          note={
            <>
              Checked on 29 September 2026: <Src href="https://learn.microsoft.com/en-us/power-platform/admin/power-automate-licensing/types">Power Automate licence types</Src>,{" "}
              <Src href="https://learn.microsoft.com/en-us/connectors/office365/">Office 365 Outlook connector</Src>,{" "}
              <Src href="https://learn.microsoft.com/en-us/defender-office-365/outbound-spam-policies-external-email-forwarding">Microsoft, external forwarding</Src>,{" "}
              <Src href="https://ifttt.com/plans">IFTTT plans</Src>.
            </>
          }
        />
      </Band>

      <Band tone="white" title="Limits, and who this is not for" answer="If your company runs everything inside Microsoft 365 and needs only simple copies of emails in Teams, Power Automate is the natural fit." narrow>
        <List
          items={[
            "AI steps can misread an email now and then; prefer drafts for anything important.",
            "Shared mailboxes depend on how they are set up in Microsoft 365; connect an account that can open them.",
            "Free: 3 apps, 2 new workflows a month, 100 runs a month.",
          ]}
        />
        <P>
          Your data is encrypted in transit and at rest, and never used to train AI models. See <In href="/security-architecture">security and your data</In>.
        </P>
      </Band>

      <Faqs items={faqs} />

      <Related
        links={[
          { href: "/integrations/outlook-to-teams", label: "Outlook to Teams", desc: "Three ways, free ones first." },
          { href: "/integrations/outlook-to-slack", label: "Outlook to Slack", desc: "No forwarding rule, so nothing bounces." },
          { href: "/teams-integration", label: "Microsoft Teams automation", desc: "Post to channels from any workflow." },
          { href: "/gmail-integration", label: "Gmail automation", desc: "The same for Gmail." },
          { href: "/lead-response-automation", label: "Lead response automation", desc: "Reply to new enquiries in minutes." },
          { href: "/pricing", label: "Pricing", desc: "Free, Starter, Growth and Enterprise." },
        ]}
      />

      <Closing title="Let new Outlook emails take care of themselves." line="Connect Outlook and describe your first workflow in one sentence." />
    </main>
    <Footer />
  </>
);

export default OutlookIntegration;
